import React, { useState, useEffect } from "react";
import { Table, Avatar, Button, Space, Typography, message, Modal } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { ReactComponent as VerifiedIcon } from "../../../Assets/images/icon/verifiedIcon.svg";
import { fetchPostList, deletePost } from "../useAPIDetails/usePostRelatedAPI";

const { Title } = Typography;
const { Text } = Typography;

const AllPostDetailsTable = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [postData, setPostData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [postId, setPostId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true); // Control Next button visibility

  // Fetch post data from API
  const loadData = async (page, size) => {
    setLoading(true);
    try {
      const result = await fetchPostList(page-1, size);
      const fetchedPosts = result.data.posts || [];
      setPostData(
        fetchedPosts.map((post) => ({
          key: post?.id,
          user: `${post?.createdByUser?.firstName || "-"} ${
            post?.createdByUser?.lastName || "-"
          }`,
          username: post?.createdByUser?.userName || "-",
          profilePicture: post?.createdByUser?.profilePicture?.fileUrl || "",
          postTitle:
            post?.postType === "RECIPE"
              ? post?.title || "-"
              : post?.caption || "-",
          type: post?.postType || "-",
          likes: post?.reactionsCount || 0,
          category: post?.categories?.length ? post?.categories?.join(", ") : "-",
          verified: post?.createdByUser?.verified || false,
        }))
      );
      // Determine if there is a next page based on the number of posts returned
      setHasNextPage(fetchedPosts.length === size); // If the length of posts equals page size, there might be more
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadData(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);

  const handleTableChange = (newPagination) => {
    setPagination({ ...pagination, current: newPagination.current });
    loadData(newPagination.current, pagination.pageSize);
  };

  const handleDelete = (key) => {
    setIsModalVisible(true);
    setPostId(key);
  };

  const handleConfirm = async () => {
    setIsModalVisible(false);
    try {
      const response = await deletePost(postId);
      message.success(response.data.message || "Post deleted successfully.");
      loadData(pagination.current, pagination.pageSize);
      setPostId(null);
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Something went wrong in deleting the post."
      );
    }
  };

  const handleRowClick = (record) => {
    const url =
      record.type === "RECIPE"
        ? `/recipe/${record.key}`
        : `/general/${record.key}`;
    window.open(url, "_blank"); // Open the URL in a new tab
  };

  const columns = [
    {
      title: "User Name",
      dataIndex: "username",
      key: "username",
      render: (text, record) => (
        <Space size="middle">
          <Avatar
            src={record.profilePicture}
            style={{ backgroundColor: "#87d068" }}
          >
            {text[0]}
          </Avatar>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span>{text}</span>
            {record.verified && (
              <VerifiedIcon style={{ width: "18px", height: "18px" }} />
            )}
          </div>
        </Space>
      ),
    },
    {
      title: "Post Title",
      dataIndex: "postTitle",
      key: "postTitle",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Likes",
      dataIndex: "likes",
      key: "likes",
      align: "center",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      align: "center",
    },
    {
      title: "",
      key: "action",
      render: (_, record) => (
        <Button
          danger
          type="link"
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(record.key);
          }}
        />
      ),
    },
  ];

  console.log("Posts are", postData);
  return (
    <div className="flex-grow-content" style={{ padding: "24px" }}>
      <Title level={4}>Post Management</Title>
      {postData.length > 0 && (
        <Table
          columns={columns}
          dataSource={postData}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: false,
            hideOnSinglePage: true,
          }}
          loading={loading}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          rowClassName={() => "clickable-row"}
          style={{
            border: "1px solid #E1E1E1",
            borderRadius: "5px",
            cursor: "pointer",
          }}
          onChange={handleTableChange}
          footer={() => (
            <div style={{ textAlign: "right" }}>
              <Button
                className="primary-button"
                variant="solid"
                disabled={!hasNextPage} // Disable Next button if no more pages
                onClick={() => {
                  const nextPage = pagination.current + 1;
                  setPagination({ ...pagination, current: nextPage });
                  loadData(nextPage, pagination.pageSize);
                }}
              >
                Next
              </Button>
            </div>
          )}
        />
      )}

      <Modal
        title="Confirm Deletion"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        centered
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="delete" type="primary" danger onClick={handleConfirm}>
            Delete
          </Button>,
        ]}
      >
        <Text>
          Are you sure you want to delete the post{" "}
          <Text strong>postid {postId}</Text>?
        </Text>
      </Modal>
    </div>
  );
};

export default AllPostDetailsTable;
